import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "wouter";

export type PrivacyChoices = { ga4: boolean; clarity: boolean; rid: boolean };

export type PrivacyControlsProps = {
  decided: boolean;
  choices: PrivacyChoices;
  gpc: boolean;
  storageError: boolean;
  onSave: (choices: PrivacyChoices) => void;
  inlineBanner?: boolean;
};

const btn =
  "min-h-11 rounded-md px-3 py-2 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-[#1a1a1a]";

export default function PrivacyControls({ decided, choices, gpc, storageError, onSave, inlineBanner = false }: PrivacyControlsProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<PrivacyChoices>({ ga4: false, clarity: false, rid: false });
  const headingRef = useRef<HTMLHeadingElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const wasOpen = useRef(false);
  const choicesRef = useRef(choices);
  choicesRef.current = choices;
  const gpcRef = useRef(gpc);
  gpcRef.current = gpc;

  const openControls = useCallback(() => {
    const active = document.activeElement;
    returnFocus.current = active instanceof HTMLElement ? active : null;
    const c = choicesRef.current;
    setDraft({ ga4: !!c.ga4, clarity: !gpcRef.current && !!c.clarity, rid: !gpcRef.current && !!c.rid });
    setOpen(true);
  }, []);

  useEffect(() => {
    window.addEventListener("gorilla-privacy-open", openControls);
    return () => window.removeEventListener("gorilla-privacy-open", openControls);
  }, [openControls]);

  useEffect(() => {
    if (open) {
      headingRef.current?.focus();
    } else if (wasOpen.current) {
      const el = returnFocus.current;
      if (el && el.isConnected) el.focus();
      else document.querySelector<HTMLElement>('footer button')?.focus();
    }
    wasOpen.current = open;
  }, [open]);

  const save = (c: PrivacyChoices) => {
    const safe = gpc ? { ...c, ga4: true, clarity: false, rid: false } : { ...c, ga4: true };
    onSave(safe);
    setOpen(false);
  };

  const row = (
    key: keyof PrivacyChoices,
    title: string,
    desc: string,
    locked: boolean,
  ) => (
    <li className="flex items-start gap-3">
      <input
        id={`privacy-${key}`}
        type="checkbox"
        className="mt-1 h-5 w-5 shrink-0 accent-[hsl(var(--primary))]"
        checked={locked ? false : draft[key]}
        disabled={locked}
        onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.checked }))}
        aria-describedby={`privacy-${key}-desc`}
        data-testid={`checkbox-privacy-${key}`}
      />
      <label htmlFor={`privacy-${key}`} className="text-sm text-white">
        <span className="font-semibold">{title}</span>
        {locked && <span className="ml-2 text-xs text-[#aaa]">(off: Global Privacy Control)</span>}
        <span id={`privacy-${key}-desc`} className="block text-xs text-[#b5b5b5] leading-relaxed">
          {desc}
        </span>
      </label>
    </li>
  );

  const wrap = "fixed inset-x-0 bottom-0 z-50";
  const panel =
    "max-h-[70dvh] overflow-y-auto border-t border-[#333] bg-[#1a1a1a] px-4 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] text-white shadow-2xl shadow-black/50 sm:px-6";

  const warn = storageError && (
    <p role="alert" className="mt-3 text-xs text-amber-300" data-testid="text-privacy-storage-error">
      Your choice could not be saved on this device. Clarity recording and RID association remain off. Google Analytics continues on public marketing pages.
    </p>
  );

  if (open) {
    return (
      <section aria-labelledby="privacy-heading" className={wrap} data-testid="section-privacy-preferences">
        <div className={panel}>
          <h2 id="privacy-heading" ref={headingRef} tabIndex={-1} className="text-base font-bold focus:outline-none">
            Privacy choices
          </h2>
          <p className="mt-1 text-xs text-[#b5b5b5]">
            Google Analytics runs on public marketing pages independently of these choices.
            Clarity recording and RID association are off unless you turn them on. You can change these choices from the footer anytime.
          </p>
          {gpc && (
            <p className="mt-3 rounded-md border border-[#333] p-2 text-xs text-[#d0d0d0]" data-testid="text-privacy-gpc">
              Your browser sends a Global Privacy Control signal, so session recording and recipient association are locked off.
              Google Analytics continues on public marketing pages.
            </p>
          )}
          <fieldset className="mt-3">
            <legend className="sr-only">Optional tracking tools</legend>
            <ul className="space-y-3">
              <li className="text-sm text-white">
                <span className="font-semibold">Google Analytics — always on public marketing pages</span>
                <span className="block text-xs text-[#b5b5b5]">Measures page visits and traffic sources independently of the banner choice.</span>
              </li>
              {row("clarity", "Microsoft Clarity session recording", "Records how you click, scroll and move through pages.", gpc)}
              {row(
                "rid",
                "Recipient association (RID)",
                "Links your visit to the intended mailing recipient of a mail piece. This is not a verified identity.",
                gpc,
              )}
            </ul>
          </fieldset>
          {warn}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button type="button" className={`${btn} bg-primary text-primary-foreground`} onClick={() => save(draft)} data-testid="button-privacy-save">
              Save choices
            </button>
            <button
              type="button"
              className={`${btn} border border-[#444] text-white hover:bg-[#2a2a2a]`}
              onClick={() => save({ ga4: false, clarity: false, rid: false })}
              data-testid="button-privacy-decline"
            >
              Reject optional tracking
            </button>
            <Link href="/privacy" className="text-xs text-[#b5b5b5] underline hover:text-primary" data-testid="link-privacy-policy">
              Privacy policy
            </Link>
          </div>
        </div>
      </section>
    );
  }

  if (!decided) {
    return (
      <section aria-labelledby="privacy-banner-heading" className={inlineBanner ? "relative z-50 w-full" : wrap} data-testid="section-privacy-banner">
        <div className={panel}>
          <h2 id="privacy-banner-heading" className="text-sm font-bold">
            Analytics on this site
          </h2>
          <p className="mt-1 text-xs text-[#b5b5b5] leading-relaxed">
            Google Analytics runs on public marketing pages regardless of your choice here.
            Accept or reject optional Microsoft Clarity session recording and RID association, which ties a visit to the
            intended mailing recipient of a mail piece (not a verified identity). These two tools remain off until you allow them.
          </p>
          {gpc && (
            <p className="mt-2 text-xs text-[#d0d0d0]">Global Privacy Control detected: session recording and RID stay off.</p>
          )}
          {warn}
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              className={`${btn} bg-primary text-primary-foreground`}
              onClick={() => save({ ga4: true, clarity: true, rid: true })}
              data-testid="button-privacy-allow"
            >
              Accept optional tracking
            </button>
            <button
              type="button"
              className={`${btn} border border-[#444] text-white hover:bg-[#2a2a2a]`}
              onClick={() => save({ ga4: true, clarity: false, rid: false })}
              data-testid="button-privacy-reject"
            >
              Reject optional tracking
            </button>
            <button
              type="button"
              className={`${btn} border border-[#444] text-white hover:bg-[#2a2a2a]`}
              onClick={openControls}
              data-testid="button-privacy-choices"
            >
              Privacy choices
            </button>
          </div>
        </div>
      </section>
    );
  }

  // No floating remnant after a saved choice. The existing footer/policy event
  // still reopens these controls for withdrawal or re-grant.
  return null;
}
